/**
 * Adaptive Indicator Calculation Throttler.
 *
 * Implements a dynamic cooldown schedule based on actual calculation duration
 * to prevent UI thread lockup on heavy multi-timeframe indicator computations.
 */

export const INDICATOR_THROTTLE_CONFIG = {
  MIN_GAP_MS: 2500,
  SAFETY_MULTIPLIER: 1.3,
} as const;

export function calculateNextGapMs(
  executionDurationMs: number,
  minGapMs: number = INDICATOR_THROTTLE_CONFIG.MIN_GAP_MS,
  safetyMultiplier: number = INDICATOR_THROTTLE_CONFIG.SAFETY_MULTIPLIER,
): number {
  return Math.max(minGapMs, executionDurationMs * safetyMultiplier);
}

export interface ThrottledTaskOptions {
  minGapMs?: number;
  safetyMultiplier?: number;
  setTimeoutFn?: (handler: () => void, timeout?: number) => any;
  clearTimeoutFn?: (id: any) => void;
  nowFn?: () => number;
  perfNowFn?: () => number;
}

export interface IndicatorExecutionContext {
  _indicatorRecomputeInFlight?: boolean;
  _indicatorRecomputePending?: boolean;
  _indicatorRecomputeLastRun?: number;
  _indicatorRecomputeNextGapMs?: number;
  _indicatorRecomputeTimer?: any;
}

/**
 * Executes or schedules an indicator recomputation function with dynamic cooldown.
 */
export function throttledIndicatorRecompute(
  ctx: IndicatorExecutionContext,
  runFn: () => Promise<void> | void,
  options: ThrottledTaskOptions = {},
): void {
  const minGapDefault =
    options.minGapMs ?? INDICATOR_THROTTLE_CONFIG.MIN_GAP_MS;
  const safetyMult =
    options.safetyMultiplier ?? INDICATOR_THROTTLE_CONFIG.SAFETY_MULTIPLIER;
  const setTimer = options.setTimeoutFn || setTimeout;
  const getNow = options.nowFn || (() => Date.now());
  const getPerfNow =
    options.perfNowFn ||
    (() => (typeof performance !== "undefined" ? performance.now() : Date.now()));

  if (ctx._indicatorRecomputeInFlight) {
    ctx._indicatorRecomputePending = true;
    return;
  }

  const now = getNow();
  const last = ctx._indicatorRecomputeLastRun || 0;
  const minGap = ctx._indicatorRecomputeNextGapMs || minGapDefault;
  const elapsed = now - last;

  if (elapsed < minGap) {
    ctx._indicatorRecomputePending = true;
    if (!ctx._indicatorRecomputeTimer) {
      ctx._indicatorRecomputeTimer = setTimer(() => {
        ctx._indicatorRecomputeTimer = null;
        if (ctx._indicatorRecomputePending) {
          ctx._indicatorRecomputePending = false;
          throttledIndicatorRecompute(ctx, runFn, options);
        }
      }, minGap - elapsed);
    }
    return;
  }

  ctx._indicatorRecomputePending = false;
  ctx._indicatorRecomputeLastRun = now;
  ctx._indicatorRecomputeInFlight = true;

  const t0 = getPerfNow();
  Promise.resolve(runFn())
    .catch((e) => {
      if (typeof console !== "undefined" && console.error) {
        console.error(e);
      }
    })
    .finally(() => {
      const duration = getPerfNow() - t0;
      ctx._indicatorRecomputeNextGapMs = calculateNextGapMs(
        duration,
        minGapDefault,
        safetyMult,
      );
      ctx._indicatorRecomputeInFlight = false;
      if (ctx._indicatorRecomputePending) {
        ctx._indicatorRecomputePending = false;
        throttledIndicatorRecompute(ctx, runFn, options);
      }
    });
}
